/**
 * A 24-hour status update.
 *
 * @typedef {object} StatusUpdate
 * @property {string}      id
 * @property {string}      userId
 * @property {'text'|'image'|'video'} type
 * @property {string}      body
 * @property {string|null} mediaUrl
 * @property {string|null} backgroundColor  Text statuses only
 * @property {string|null} font             Text statuses only
 * @property {number}      viewCount
 * @property {boolean}     viewedByMe
 * @property {Date}        createdAt
 * @property {Date}        expiresAt
 */

import { toDate } from '../utils/time.js'

export const STATUS_TTL_MS = 24 * 60 * 60 * 1000

/**
 * @param {string} id
 * @param {object} raw
 * @param {string} [currentUserId]
 * @returns {StatusUpdate}
 */
export function toStatus(id, raw = {}, currentUserId) {
  const createdAt = toDate(raw.createdAt ?? raw.created_at) ?? new Date()
  return {
    id,
    userId:          raw.userId ?? raw.user_id ?? '',
    type:            raw.type ?? 'text',
    body:            raw.body ?? '',
    mediaUrl:        raw.mediaUrl ?? raw.media_url ?? null,
    backgroundColor: raw.backgroundColor ?? raw.background_color ?? null,
    font:            raw.font ?? null,
    viewCount:       raw.viewCount ?? raw.view_count ?? 0,
    viewedByMe:      Boolean(currentUserId && (raw.viewedBy ?? []).includes(currentUserId)),
    createdAt,
    expiresAt:       toDate(raw.expiresAt ?? raw.expires_at) ?? new Date(createdAt.getTime() + STATUS_TTL_MS)
  }
}

export function isExpired(status, now = new Date()) {
  return status.expiresAt.getTime() <= now.getTime()
}

/**
 * Group a flat list into one entry per author, which is how the status tray
 * is rendered: one ring per person, tapping through their updates in order.
 */
export function groupByAuthor(updates) {
  const byAuthor = new Map()
  for (const update of updates) {
    if (!byAuthor.has(update.userId)) byAuthor.set(update.userId, [])
    byAuthor.get(update.userId).push(update)
  }
  return [...byAuthor.entries()].map(([userId, items]) => {
    const sorted = items.sort((a, b) => a.createdAt - b.createdAt)
    return {
      userId,
      updates:    sorted,
      allViewed:  sorted.every(u => u.viewedByMe),
      latestAt:   sorted[sorted.length - 1].createdAt
    }
  }).sort((a, b) => b.latestAt - a.latestAt)
}
